import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { login as apiLogin, setAuthToken, setUnauthorizedHandler } from '../lib/api';
import { LoginInput, User } from '../types';

const STORAGE_KEY = 'handcrafted.auth';

interface StoredAuth {
  token: string;
  user: User;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (input: LoginInput) => Promise<User>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuth;
    if (!parsed?.token || !parsed?.user) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // The token is installed here, in the lazy initializer, NOT in an effect.
  // React flushes child effects before the parent's, so a page that fetches
  // on mount would otherwise send its first request before the provider had
  // restored the token — arriving at the API as anonymous. Public endpoints
  // still answer, which is why that stayed invisible until an admin-only
  // field on a public route came back missing on every reload.
  const [auth, setAuth] = useState<StoredAuth | null>(() => {
    const stored = readStoredAuth();
    setAuthToken(stored?.token ?? null);
    return stored;
  });

  // Keeps the token in step with later changes (login, logout, 401 clear).
  useEffect(() => {
    setAuthToken(auth?.token ?? null);
  }, [auth]);

  const login = useCallback(async (input: LoginInput) => {
    const result = await apiLogin(input);
    const stored: StoredAuth = { token: result.token, user: result.user };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    setAuth(stored);
    return result.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setAuth(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      localStorage.removeItem(STORAGE_KEY);
      setAuth(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: auth?.user ?? null,
      token: auth?.token ?? null,
      isAuthenticated: Boolean(auth),
      login,
      logout,
    }),
    [auth, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
